"use client";

import { useEffect, useMemo, useRef } from "react";
import { cx } from "@/lib/tailwind-utils";

// Dial travel, in pixels along the arc, per doubling of zoom. Zoom is laid out
// in log space so every doubling feels the same under the finger.
const PX_PER_DOUBLING = 100;
// Hold this long on the lens buttons to bring up the dial without dragging.
const LONG_PRESS_MS = 300;
// Movement that turns a press into a scrub instead of a tap.
const DRAG_SLOP_PX = 6;

// Arc geometry: a large circle whose top edge peeks out above the buttons,
// like the iPhone camera's zoom wheel.
const DIAL_WIDTH_PX = 340;
const DIAL_HEIGHT_PX = 104;
const DIAL_RADIUS_PX = 320;
// Room above the arc for the live zoom readout.
const DIAL_ARC_TOP_PX = 30;
const DEG_PER_DOUBLING = PX_PER_DOUBLING / DIAL_RADIUS_PX * (180 / Math.PI);
const TICKS_PER_DOUBLING = 12;

// Whether this device has a telephoto lens behind its main camera. Safari
// lists each lens (and the multi-lens "virtual" cameras) as its own device;
// a phone with a telephoto lists seven cameras even when labels are localised.
export function hasTelephotoLens(devices: MediaDeviceInfo[]): boolean {
  return devices.some(d => /tele|triple/i.test(d.label)) || devices.length >= 7;
}

// The one-tap lens stops, like the iPhone's ".5  1  2  3" row. Built from what
// the open camera actually reports: an ultra-wide shows up as a zoom range
// starting below 1 (Safari opens the multi-lens camera and maps the ultra-wide
// to 0.5), and a telephoto from the lens list. Lenses switch automatically as
// the zoom crosses them.
export function zoomPresets(min: number, max: number, {
  telephoto = false
}: {
  telephoto?: boolean;
} = {}): number[] {
  if (max - min < 0.25) return [];
  const presets: number[] = [];
  if (min < 0.95) presets.push(Math.ceil(min * 10) / 10);
  if (min <= 1.01 && max >= 1) presets.push(1);
  if (max >= 2) presets.push(2);
  if (telephoto && max >= 3) presets.push(3);
  return presets;
}

// Whether moving from prev to next passed over (or landed on) a preset, so a
// gesture can give a haptic tick there.
export function crossedPreset(prev: number, next: number, presets: number[]): boolean {
  const lo = Math.min(prev, next);
  const hi = Math.max(prev, next);
  return presets.some(p => p > lo && p <= hi && prev !== p);
}
export function formatZoom(zoom: number): string {
  const rounded = Math.round(zoom * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return rounded < 1 ? text.replace(/^0/, "") : text;
}
interface CameraZoomControlProps {
  zoom: number;
  min: number;
  max: number;
  presets: number[];
  // The dial shows while any zoom gesture is in progress (pinch, scroll,
  // scrubbing here) and briefly after.
  dialVisible: boolean;
  disabled?: boolean;
  onSelectPreset: (zoom: number) => void;
  // Called on every move while scrubbing the buttons/dial.
  onScrub: (zoom: number) => void;
}
const CameraZoomControl: React.FC<CameraZoomControlProps> = ({
  zoom,
  min,
  max,
  presets,
  dialVisible,
  disabled = false,
  onSelectPreset,
  onScrub
}) => {
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startZoom: number;
    scrubbing: boolean;
  } | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A scrub ends with a click on whichever button the finger started on;
  // swallow it so letting go doesn't jump to that preset.
  const suppressClickRef = useRef(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  useEffect(() => () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  }, []);

  // Ticks sit at fixed angles on the wheel; zooming only rotates it.
  const ticks = useMemo(() => {
    if (!presets.length) return [];
    const logMin = Math.log2(min);
    const steps = Math.floor((Math.log2(max) - logMin) * TICKS_PER_DOUBLING);
    const angleOf = (value: number) => (Math.log2(value) - logMin) * DEG_PER_DOUBLING;
    const minor = Array.from({
      length: steps + 1
    }, (_, i) => {
      const value_0 = 2 ** (logMin + i / TICKS_PER_DOUBLING);
      return {
        angle: angleOf(value_0),
        value: value_0
      };
    }).filter(({
      value: value_1
    }) =>
    // Leave room for the preset marks.
    presets.every(p => Math.abs(Math.log2(value_1 / p)) > 0.04));
    return [...minor.map(t => ({
      ...t,
      preset: false
    })), ...presets.map(p_0 => ({
      angle: angleOf(p_0),
      value: p_0,
      preset: true
    }))];
  }, [min, max, presets]);
  if (!presets.length) return null;
  const activeIndex = presets.reduce((best, p_1, i_0) => zoom >= p_1 - 0.05 ? i_0 : best, 0);
  const wheelRotation = -(Math.log2(zoom) - Math.log2(min)) * DEG_PER_DOUBLING;
  const fadeEdges = "linear-gradient(to right, transparent, black 24%, black 76%, transparent)";
  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const beginScrub = (target: HTMLElement) => {
    const drag = dragRef.current;
    if (!drag || drag.scrubbing) return;
    drag.scrubbing = true;
    suppressClickRef.current = true;
    target.setPointerCapture?.(drag.pointerId);
    navigator.vibrate?.(8);
    // Bring the dial up even before the finger moves.
    onScrub(drag.startZoom);
  };
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || dragRef.current) return;
    suppressClickRef.current = false;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startZoom: zoomRef.current,
      scrubbing: false
    };
    const target_0 = e.currentTarget;
    clearLongPress();
    longPressTimerRef.current = setTimeout(() => beginScrub(target_0), LONG_PRESS_MS);
  };
  const handlePointerMove = (e_0: React.PointerEvent<HTMLDivElement>) => {
    const drag_0 = dragRef.current;
    if (!drag_0 || drag_0.pointerId !== e_0.pointerId) return;
    const dx = e_0.clientX - drag_0.startX;
    if (!drag_0.scrubbing) {
      if (Math.abs(dx) < DRAG_SLOP_PX) return;
      clearLongPress();
      beginScrub(e_0.currentTarget);
    }
    // Dragging left turns the wheel toward the telephoto end, as on iPhone.
    onScrub(drag_0.startZoom * 2 ** (-dx / PX_PER_DOUBLING));
  };
  const endDrag = (e_1: React.PointerEvent<HTMLDivElement>) => {
    const drag_1 = dragRef.current;
    if (!drag_1 || drag_1.pointerId !== e_1.pointerId) return;
    clearLongPress();
    if (drag_1.scrubbing) {
      e_1.currentTarget.releasePointerCapture?.(drag_1.pointerId);
    }
    dragRef.current = null;
  };
  return (
    // `dark` keeps theme tokens on their dark-palette values: the camera
    // chrome always sits on a black preview, where the accent must read yellow.
    <div className="dark relative flex touch-none select-none flex-col items-center" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}
    // The preview behind treats touches as pinch / tap-to-focus; keep this
    // control's touches to itself.
    onTouchStart={e_2 => e_2.stopPropagation()} onTouchMove={e_3 => e_3.stopPropagation()} onTouchEnd={e_4 => e_4.stopPropagation()}>
      {/* Zoom wheel: an arc of ticks that rotates under a fixed centre mark */}
      <div aria-hidden className={cx("pointer-events-none absolute bottom-0 left-1/2 origin-bottom -translate-x-1/2 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.2,0.9,0.3,1.2)]", dialVisible ? "translate-y-0 scale-100 opacity-100" : "translate-y-4 scale-90 opacity-0")} style={{
        width: DIAL_WIDTH_PX,
        height: DIAL_HEIGHT_PX
      }}>
        <div className="absolute inset-0 overflow-hidden" style={{
          maskImage: fadeEdges,
          WebkitMaskImage: fadeEdges
        }}>
          {/* Wheel face */}
          <div className="absolute left-1/2 -translate-x-1/2 rounded-full border-t border-white/20 bg-black/55 shadow-[inset_0_12px_24px_-12px_rgba(255,255,255,0.25)] backdrop-blur-xl" style={{
            top: DIAL_ARC_TOP_PX,
            width: DIAL_RADIUS_PX * 2,
            height: DIAL_RADIUS_PX * 2
          }} />
          {/* Ticks + lens labels, pivoting on the wheel's centre */}
          <div className="absolute" style={{
            left: DIAL_WIDTH_PX / 2,
            top: DIAL_ARC_TOP_PX + DIAL_RADIUS_PX,
            transform: `rotate(${wheelRotation}deg)`,
            willChange: "transform"
          }}>
            {ticks.map(tick => tick.preset ? <div key={`p-${tick.value}`}>
                  <div className="absolute left-0 top-0 h-3.5 w-[2px] origin-top-left rounded-full bg-white" style={{
                transform: `rotate(${tick.angle}deg) translate(-50%, -${DIAL_RADIUS_PX - 6}px)`
              }} />
                  <div className="absolute left-0 top-0 w-8 origin-top-left text-center text-caption-emphasis leading-none text-white" style={{
                transform: `rotate(${tick.angle}deg) translate(-50%, -${DIAL_RADIUS_PX - 26}px)`
              }}>
                    {formatZoom(tick.value)}×
                  </div>
                </div> : <div key={`t-${tick.value}`} className="absolute left-0 top-0 h-2 w-px origin-top-left bg-white/45" style={{
              transform: `rotate(${tick.angle}deg) translate(-50%, -${DIAL_RADIUS_PX - 6}px)`
            }} />)}
          </div>
        </div>

        {/* Live readout + centre mark */}
        <div className="absolute left-1/2 top-0 flex -translate-x-1/2 flex-col items-center">
          {/* Dark pill so the readout stays legible over bright scenes */}
          <span className="rounded-full bg-black/60 px-2 py-0.5 text-caption-strong tabular-nums text-status-warning-text backdrop-blur-sm">
            {formatZoom(zoom)}×
          </span>
          <div className="mt-1 size-0 border-x-[5px] border-t-[6px] border-x-transparent border-t-status-warning-text" />
          <div className="h-5 w-[2px] rounded-full bg-status-warning-text" />
        </div>
      </div>

      {/* Lens buttons */}
      <div role="group" aria-label="Zoom" className={cx("flex items-center gap-1.5 rounded-full bg-black/30 p-1 shadow-lg ring-1 ring-white/10 backdrop-blur-xl transition-[opacity,transform] duration-200 ease-out", dialVisible ? "pointer-events-none translate-y-2 scale-75 opacity-0" : "scale-100 opacity-100", disabled && "opacity-50")}>
        {presets.map((preset, i_1) => {
          const active = i_1 === activeIndex;
          return <button key={preset} type="button" disabled={disabled} aria-label={`Zoom ${formatZoom(preset)}x`} aria-pressed={active} onClick={() => {
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            onSelectPreset(preset);
          }} className={cx("flex items-center justify-center rounded-full font-semibold tabular-nums transition-all duration-200 ease-out active:scale-90", active ? "size-11 bg-black/60 text-caption-strong text-status-warning-text" : "size-8 bg-black/45 text-caption-emphasis text-white hover:bg-black/70")}>
              {active ? `${formatZoom(zoom)}×` : formatZoom(preset)}
            </button>;
        })}
      </div>
    </div>
  );
};
export default CameraZoomControl;